import React from 'react';
import { useParams } from 'react-router-dom';
import IdentityCard from '../components/IdentityCard';

// Case Study Modal Content
const caseStudyModalContent = {
  maidr: `
    <div class="project-modal-content">
      <section class="modal-section">
        <div class="modal-tags">
          <span class="tag">Conversational AI</span>
          <span class="tag">Accessibility</span>
          <span class="tag">Mixed Methods Research</span>
          <span class="tag">Data Visualization</span>
          <span class="tag">User-Centered Design</span>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Project Context</h3>
        <div class="modal-content-block">
          <p class="modal-text"><strong>My Role:</strong> Research Assistant on the MAIDR team, supporting the Principal Investigator with user experience research methodologies and analysis.</p>
          <p class="modal-text">The MAIDR project addressed a critical research gap in accessible data visualization: <strong>While large language models (LLMs) could generate text descriptions of complex data visualizations, there was no systematic understanding of how Blind and Low Vision (BLV) users would interact with, trust, customize, or verify these AI-generated outputs</strong>.</p>
          <p class="modal-text">The research investigated how different sensory modalities (tactile, auditory, and braille) supported users' cognitive and emotional engagement with data, conducted across two sequential studies.</p>
          <p class="modal-text"><strong>My Specific Contributions:</strong> As an RA, I co-designed the experimental methodology, developed theoretical frameworks for accessible AI-human interaction, designed and implemented user experience protocols, and performed comprehensive qualitative analysis of user interactions. I employed mixed-methods approaches that allowed the team to triangulate findings and validate qualitative insights with quantitative data.</p>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Study Overview</h3>
        <div class="modal-content-block">
          <table style="width: 100%; border-collapse: collapse; margin: 1rem 0;">
            <tr style="background-color: #f8fafc;">
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Study</th>
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Focus</th>
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">My Contributions</th>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Study 1</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">User interaction with AI-generated descriptions</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Conducted semi-structured interviews, performed thematic analysis, contributed to AUS survey design</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Study 2</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">User performance across tactile, sonified, and braille modalities</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Led creation of research materials, created STG materials, conducted participant sessions, performed quantitative and qualitative analysis, led manuscript development</td>
            </tr>
          </table>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Study 1: My Contributions</h3>
        <div class="modal-content-block">
          <p class="modal-text"><strong>My Role:</strong> I conducted semi-structured interviews with participants after they explored the maidrAI system, focusing on how they adapted or prompted the AI, strategies they used to verify output trustworthiness, and preferences around description styles and length.</p>
          
          <p class="modal-text"><strong>Thematic Analysis Results:</strong> Through my analysis, key themes emerged:</p>
          <ul>
            <li><strong>Layered Summaries:</strong> Users preferred quick summaries with the option to drill down into detail. My analysis revealed that cognitive load management was crucial (users needed to control the amount of information they received at any given moment).</li>
            <li><strong>Verification Rituals:</strong> Users routinely "double-checked" AI-generated information, indicating the need for transparency features.</li>
            <li><strong>Cognitive Load:</strong> Verbosity increased mental effort, especially when navigating complex datasets.</li>
          </ul>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Study 2: My Contributions</h3>
        <div class="modal-content-block">
          <p class="modal-text"><strong>My Role:</strong> I designed and created tactile STG (swell-form) materials, conducted participant sessions across three non-visual modalities, and performed qualitative analysis of user strategies.</p>
          
          <p class="modal-text"><strong>Key Findings from My Analysis:</strong></p>
          <ul>
            <li><strong>Modality Preferences:</strong> Each modality (tactile, sonified, braille) served different user needs and contexts</li>
            <li><strong>User Strategies:</strong> Participants developed unique approaches for navigating and interpreting data across modalities</li>
            <li><strong>Accessibility Design:</strong> No single modality was universally superior - user choice was essential</li>
          </ul>
          
          <div style="text-align: center; margin: 2rem 0;">
            <img src="/img/maidr/histcognition.jpg" alt="Bar chart showing histogram cognition study results" style="max-width: 800px; height: auto; border-radius: 8px;" />
          </div>
          
          <p class="modal-text"><strong>Methodology and Implementation:</strong></p>
          <ul>
            <li><strong>Co-developed experimental protocol:</strong> Working closely with the PI to ensure methodological rigor</li>
            <li><strong>STG materials development:</strong> Designed and created the tactile STG (swell-form) materials for the study</li>
            <li><strong>Protocol execution:</strong> Collaborated with the PI to implement the study protocol</li>
            <li><strong>Participant sessions:</strong> Conducted detailed sessions with BLV participants across three non-visual modalities</li>
            <li><strong>Data analysis:</strong> Performed quantitative and qualitative analysis of user performance and strategies</li>
            <li><strong>Manuscript leadership:</strong> Led the manuscript development process</li>
          </ul>
          
          <div style="text-align: center; margin: 2rem 0;">
            <img src="/img/maidr/maidr-hands.jpg" alt="Participant exploring tactile data visualization using swell-form paper" style="max-width: 800px; height: auto; border-radius: 8px;" />
          </div>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Dashboard Development</h3>
        <div class="modal-content-block">
          <p class="modal-text">Through my contributions to this collaborative research project, the team developed insights into how BLV users interact with AI-generated descriptions and different sensory modalities for data interpretation.</p>
          
          <p class="modal-text">I developed the interactive dashboard frontend by integrating the MAIDR backend system created by the research team. My design decisions focused on creating an accessible, user-controlled interface with:</p>
          
          <ul>
            <li><strong>Keyboard-first navigation architecture</strong> ensuring all dashboard functions are accessible without mouse interaction</li>
            <li><strong>Clear visual hierarchy and contrast</strong> for users with low vision, with prominent buttons and readable typography</li>
            <li><strong>Intuitive upload workflow</strong> with drag-and-drop support and clear progress indicators for dataset processing</li>
            <li><strong>Accessible control panels</strong> with proper ARIA labels and screen reader compatibility for all interactive elements</li>
            <li><strong>Responsive layout design</strong> that works across different screen sizes and assistive technology configurations</li>
          </ul>
          
          <p class="modal-text">The dashboard interface prioritizes discoverability and user control, ensuring that the powerful backend capabilities developed by the team are presented through an interface that BLV users can navigate independently and efficiently.</p>
          
          <div style="text-align: center; margin: 2rem 0;">
            <a href="https://xabilitylab.shinyapps.io/a11y_dashboard/" target="_blank" rel="noopener noreferrer" style="display: inline-block; text-decoration: none;">
              <img src="/img/maidr/dashboard.jpg" alt="Screenshot of MAIDR accessible dashboard showing interface for data visualization with multiple modality options" style="max-width: 800px; height: auto; border-radius: 8px; cursor: pointer;" />
            </a>
          </div>
          
          <div style="text-align: center; margin: 2rem 0;">
            <a href="https://xabilitylab.shinyapps.io/a11y_dashboard/" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
              View Interactive Dashboard
            </a>
          </div>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Study 2: Quantitative Results</h3>
        <div class="modal-content-block">
          <p class="modal-text">My quantitative analysis revealed significant insights into modality performance trade-offs:</p>
          
          <table style="width: 100%; border-collapse: collapse; margin: 1rem 0;">
            <tr style="background-color: #f8fafc;">
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Modality</th>
              <th style="padding: 0.75rem; border: 1px solid #000000;">Accuracy</th>
              <th style="padding: 0.75rem; border: 1px solid #000000;">Response Time</th>
              <th style="padding: 0.75rem; border: 1px solid #000000;">Confidence</th>
              <th style="padding: 0.75rem; border: 1px solid #000000;">Cognitive Load</th>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Tactile</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">78%</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Slow (45s avg)</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">High (4.2/5)</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Moderate</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Sonification</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">72%</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Fast (18s avg)</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Moderate (3.8/5)</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Low</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Braille</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">85%</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Slow (52s avg)</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Moderate (3.9/5)</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">High</td>
            </tr>
          </table>
          
          <p class="modal-text"><strong>Statistical Analysis:</strong> Using the Friedman Test, I found no statistically significant difference in raw accuracy across modalities (p > 0.05), but significant differences in confidence and speed (p < 0.01). This reinforced that user choice and context matter more than inherent modality superiority.</p>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Personal Development Insights</h3>
        <div class="modal-content-block">
          <ul>
            <li><strong>Mixed-methods research enables comprehensive understanding</strong> of complex accessibility challenges, combining the depth of qualitative insights with the rigor of quantitative validation.</li>
            <li><strong>User control is more important than system accuracy</strong> in building trust with AI systems, especially for marginalized users who have experienced exclusion from technology.</li>
            <li><strong>Modality diversity reflects cognitive diversity</strong>, and systems should support multiple interaction strategies rather than forcing users into standardized workflows.</li>
            <li><strong>Accessibility research requires methodological innovation</strong> to capture the full spectrum of user experience, including behavioral patterns that may not be captured through traditional usability methods.</li>
          </ul>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Final Reflection</h3>
        <div class="modal-content-block">
          <p class="modal-text">The MAIDR project demonstrates that accessible AI systems require more than technical implementation. They need deep understanding of user trust dynamics, cognitive load management, and modality-specific interaction patterns. The sequential mixed-methods approach revealed that BLV users have diverse strategies for engaging with data, and that the most effective systems are those that adapt to individual preferences rather than imposing standardized solutions.</p>
          <p class="modal-text"><strong>Key Learning:</strong> True accessibility in AI-driven systems emerges from treating users as co-designers who have unique expertise in their own interaction needs. By centering user control, transparency, and flexibility, our collaborative work on MAIDR became more than a research tool. It became a model for how AI systems can be designed to empower rather than exclude marginalized users. This collaborative project reinforced my understanding that the best accessibility solutions come from genuine collaboration with users, not just testing on them.</p>
        </div>
      </section>
      <div class="modal-footer">
        <a href="/" class="btn btn-secondary">Back to HOME</a>
      </div>
    </div>
  `,
  clearminds: `
    <div class="project-modal-content">
      <section class="modal-section">
        <div class="modal-tags">
          <span class="tag">Mental Health UX</span>
          <span class="tag">Empathic Design</span>
          <span class="tag">Emotional Interfacing</span>
          <span class="tag">User Research</span>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Project Context</h3>
        <div class="modal-content-block">
          <p>Mental health apps often face two core issues:</p>
          <ul>
            <li>Users struggle to trust generalized, AI-driven recommendations.</li>
            <li>Tracking emotional progress feels either too mechanical or too overwhelming.</li>
          </ul>
          <p>When I conceptualized ClearMinds, I asked:<br>
          <strong>Can we create a system that feels structured yet human, scientific yet emotionally validating?</strong></p>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Research Objective</h3>
        <div class="modal-content-block">
          <blockquote>
            <p><strong>How can mental wellness platforms balance structure, trust, and emotional nuance to support sustained, meaningful user engagement?</strong></p>
          </blockquote>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Research Strategy Overview</h3>
        <div class="modal-content-block">
          <p>Given the complexity, I structured the work into two sequential studies that informed each other:</p>
          <table style="width: 100%; border-collapse: collapse; margin: 1rem 0;">
            <tr style="background-color: #f8fafc;">
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Phase</th>
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Focus</th>
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Methods</th>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">1</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Understand User Pain Points</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Persona Building, Journey Mapping</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">2</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Ideate Structure</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Affinity Diagramming, Storyboarding</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">3</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Prototype and Test</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Paper Wireframing, Low-Fidelity UX Testing</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">4</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Refine and Evaluate</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">High-Fidelity Prototyping, Accessibility Audits, Usability Testing</td>
            </tr>
          </table>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Phase 1: Discovery — Understanding Emotional and Structural Needs</h3>
        <div class="insight-card">
          <h4 class="insight-title">Persona Creation</h4>
          <p>Through early interviews and secondary research, I created core user personas:</p>
          <ul>
            <li><strong>Trust-Seeker</strong>: Skeptical of digital therapy.</li>
            <li><strong>Structure-Seeker</strong>: Wants clear progress pathways.</li>
            <li><strong>Flexibility-Seeker</strong>: Needs adaptable emotional tracking tools.</li>
          </ul>
        </div>
        <div style="text-align: center; margin: 2rem 0;">
                      <img src="/img/clearminds/persona.png" alt="User persona card for the ClearMinds mental health platform showing a detailed profile of a potential user. The persona includes demographic information, behavioral patterns, emotional needs, and pain points related to mental health app usage. The card features a cartoon avatar and lists key characteristics such as trust issues with AI recommendations, desire for flexible structure, and need for emotional validation. This persona represents the target user archetype that informed the design decisions for creating a mental health platform that balances structure with personalization." style="max-width: 800px; height: auto; border-radius: 8px;" />
        </div>
        <div class="insight-card">
          <h4 class="insight-title">Journey Mapping</h4>
          <p>Mapped emotional highs and lows across user experiences with existing apps.</p>
          <p><strong>Critical "drop-off" points:</strong></p>
          <ul>
            <li>Feeling misunderstood by generic AI feedback.</li>
            <li>Overwhelmed by rigid task flows without customization.</li>
          </ul>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Phase 2: Ideation — Structuring Flexible Support</h3>
        <div class="insight-card">
          <h4 class="insight-title">Affinity Diagramming</h4>
          <p>Grouped user needs into thematic clusters:</p>
          <table style="width: 100%; border-collapse: collapse; margin: 1rem 0;">
            <tr style="background-color: #f8fafc;">
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Theme</th>
              <th style="padding: 0.75rem; border: 1px solid #000000; text-align: left;">Implication</th>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Trust & Transparency</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Show users how recommendations are formed</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Flexible Structure</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Allow self-paced, customizable progress tracking</td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Empowerment</td>
              <td style="padding: 0.75rem; border: 1px solid #000000;">Offer users second-opinion mechanisms and therapist switch options</td>
            </tr>
          </table>
        </div>
        <div class="insight-card">
          <h4 class="insight-title">Storyboarding</h4>
          <p>Visualized use cases where structured guidance adapts dynamically to emotional states.</p>
        </div>
        <div style="text-align: center; margin: 2rem 0;">
                      <img src="/img/clearminds/storyboard.png" alt="Storyboard showing the user journey for the ClearMinds mental health platform. The visual narrative depicts a user's experience from initial app discovery through daily engagement with the platform. The storyboard includes multiple panels showing key touchpoints such as onboarding, mood tracking, AI recommendations, and progress visualization. Each panel illustrates how the platform adapts to user emotional states and provides flexible structure while maintaining trust and transparency. This storyboard demonstrates the user-centered design approach that prioritizes emotional authenticity and personalized support." style="max-width: 800px; height: auto; border-radius: 8px;" />
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Phase 3: Prototyping — Testing Core Concepts</h3>
        <div class="insight-card">
          <h4 class="insight-title">Paper Wireframes and Low-Fidelity Prototypes</h4>
          <ul>
            <li>Created progress dashboards</li>
            <li>Designed structured task boards with opt-out flexibility</li>
            <li>Developed "therapist transparency" profiles for clearer communication</li>
          </ul>
        </div>
        <div class="insight-card">
          <h4 class="insight-title">Usability Testing</h4>
          <p>Tested wireframes with real users.</p>
          <p><strong>Major learnings:</strong></p>
          <ul>
            <li>Needed visual metaphors for emotional states (e.g., weather systems).</li>
            <li>Mandatory task tracking discouraged engagement; flexibility was crucial.</li>
          </ul>
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Phase 4: Refinement — Building Trust and Accessibility</h3>
        <div class="insight-card">
          <h4 class="insight-title">High-Fidelity Prototyping</h4>
          <ul>
            <li>Integrated second-opinion features for therapy paths.</li>
            <li>Gamified emotional tracking with visual progress markers.</li>
            <li>Added optionality across every structure (skip, edit, pause).</li>
          </ul>
        </div>
        <div class="insight-card">
          <h4 class="insight-title">Accessibility Audits</h4>
          <ul>
            <li>Ensured WCAG 2.1 AA compliance.</li>
            <li>Designed dark mode and dyslexia-friendly font options.</li>
          </ul>
        </div>
        <div style="text-align: center; margin: 2rem 0;">
                      <img src="/img/clearminds/hifi.jpg" alt="High-fidelity prototype screenshot of the ClearMinds mental health platform interface. The design shows a clean, modern mobile app interface with a dashboard displaying mood tracking, progress visualization, and AI recommendation features. The interface includes weather-based emotional metaphors, customizable progress markers, and transparency controls for AI recommendations. The design emphasizes accessibility with dark mode options and dyslexia-friendly typography. This prototype demonstrates the final design solution that balances structured guidance with flexible personalization for mental health support." style="max-width: 800px; height: auto; border-radius: 8px;" />
        </div>
      </section>
      <section class="modal-section">
        <h3 class="modal-section-title">Reflections on Research Process</h3>
        <div class="insight-card">
          <h4 class="insight-title">What Worked</h4>
          <ul>
            <li>Building transparency and control built trust faster than "smarter" AI claims.</li>
            <li>Flexible structure encouraged emotional authenticity and long-term engagement.</li>
          </ul>
        </div>
        <div class="insight-card">
          <h4 class="insight-title">Challenges</h4>
          <ul>
            <li>Balancing emotional depth with UX simplicity required careful emotional design without over-engineering.</li>
            <li>Habit formation needed gentle nudging, not rigid enforcement.</li>
          </ul>
        </div>
        <div class="insight-card">
          <h4 class="insight-title">Personal Learning</h4>
          <p>Mental health UX must respect emotional messiness — users thrive when systems flex to accommodate, not correct, their inner journeys.</p>
        </div>
      </section>
      <div class="modal-footer">
        <a href="/" class="btn btn-secondary">Back to HOME</a>
      </div>
    </div>
  `,
} as const;

export type HomeCaseStudyCategory = 'UI/UX Design' | 'UX Research' | 'Product Design';

export type HomeCaseStudyData = {
  id: string;
  title: string;
  image: string;
  description: string;
  tags: string[];
  categories: HomeCaseStudyCategory[];
  showModal: boolean;
  modalTitle: string;
  technologies: string[];
  date: string;
  modalContent: string;
};

export const caseStudyHomeData: HomeCaseStudyData[] = [
  {
    id: 'anchor',
    title: 'Anchor: An AI Co-Navigator for Developmental Topographical Disorientation',
    image: '/img/anchor/anchor-cover.svg',
    description: 'An auto-ethnographic case study: an AI co-navigator built across Apple Watch, iPhone, and AirPods for people who can see a map fine and still can’t turn it into a direction. Includes a working interactive prototype.',
    tags: ['Human-AI Collaboration', 'Auto-Ethnography', 'UX Design', 'UI Design', 'Product Design', 'Interface Design'],
    categories: ['UI/UX Design', 'Product Design'],
    showModal: false,
    modalTitle: 'Anchor: An AI Co-Navigator for Developmental Topographical Disorientation',
    technologies: ['React', 'TypeScript', 'SVG', 'Apple Watch', 'iPhone', 'AirPods'],
    date: '2026 (Independent Research)',
    modalContent: '',
  },
  {
    id: 'dixtral',
    title: 'DIXTRAL: Accessible 3D Visualization',
    image: '/img/dixtral/dixtral.png',
    description: 'Built a web tool that lets blind and low-vision researchers independently explore 3D scientific plots using sound, keyboard navigation, and text. Published at ASSETS 2025 and CHI 2026.',
    tags: ['Data Visualization', 'Accessibility', 'Co-Design', 'UI Design', 'Interface Design', 'UX Research'],
    categories: ['UI/UX Design', 'UX Research'],
    showModal: false,
    modalTitle: 'DIXTRAL: Accessible 3D Data Visualization',
    technologies: ['JavaScript', 'WebGL', 'Web Audio API', 'ARIA'],
    date: 'May 2025 - Present (Part of my Doctoral Thesis)',
    modalContent: '',
  },
  {
    id: 'vr-exergames',
    title: 'Playing Without Barriers: Accessible VR Exergames',
    image: '/img/vrbanner.jpg',
    description: 'Built two accessible VR sports games (table tennis + boxing) for BLV players using spatial audio, haptics, and co-design. All 6 user study participants reached MVPA thresholds. Published at ASSETS 2024 and ASSETS 2025.',
    tags: ['Health & Fitness', 'Accessibility', 'Co-Design', 'UX Design', 'Interface Design', 'UX Research'],
    categories: ['UI/UX Design', 'UX Research'],
    showModal: false,
    modalTitle: 'Accessible VR Exergames: Enhancing Fitness and Autonomy for BLV Users',
    technologies: ['Unity', 'C#', 'Oculus SDK', 'bHaptics Studio', 'OpenXR'],
    date: 'March 2023 - January 2024 (Part of my Doctoral Thesis)',
    modalContent: '',
  },
  {
    id: 'maidr',
    title: 'MAIDR: Multimodal Data Access',
    image: '/img/maidrbanner.jpg',
    description: 'Conducted multi-phase research on how Blind and Low Vision (BLV) users engage with data through AI descriptions and multimodal interfaces. Independently created an accessible dashboard to facilitate easy user interaction.',
    tags: ['Data Visualization', 'Accessibility', 'Mixed Methods', 'UX Research'],
    categories: ['UX Research'],
    showModal: true,
    modalTitle: 'MAIDR: Multimodal Access and Interface for Data Representation',
    technologies: ['R Shiny', 'Python', 'TensorFlow', 'Pandas', 'NumPy', 'Matplotlib'],
    date: 'January 2024 - Present',
    modalContent: caseStudyModalContent.maidr,
  },
  {
    id: 'clearminds',
    title: 'ClearMinds: Mental Health UX',
    image: '/img/clearmindsbanner.jpg',
    description: 'Developed a mental health platform centered on trust and emotional awareness, balancing structured progress tracking with personalized flexibility for individuals during the 2020 pandemic.',
    tags: ['Mental Health', 'User Research', 'UX Design', 'UI Design', 'Product Design'],
    categories: ['UI/UX Design', 'Product Design'],
    showModal: true,
    modalTitle: 'ClearMinds: Mental Health UX Design Through Empathic Research',
    technologies: ['Figma', 'Adobe XD', 'React', 'TypeScript', 'CSS3'],
    date: 'March 2023 - June 2023',
    modalContent: caseStudyModalContent.clearminds,
  },
];

const CaseStudy: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const caseStudies: Record<string, any> = {
    'maidr': {
      title: 'MAIDR: Multimodal Accessible Interface for Data Representation',
      tags: ['Research Ethics', 'Conversational AI', 'Accessibility', 'Mixed Methods Research', 'Data Visualization'],
      timeline: 'January 2024 - Present',
      image: '/img/maidrbanner.jpg',
      content: `
        <h2>Project Context</h2>
        <p><strong>My Role:</strong> Research Assistant supporting the Principal Investigator with user experience research methodologies and analysis.</p>
        <p>The MAIDR project addressed a critical research gap in accessible data visualization:</p>
        <p>While large language models (LLMs) could generate text descriptions of complex data visualizations, there was no structured understanding of how Blind and Low Vision (BLV) users would interact with, trust, customize, or verify these outputs.</p>
        <p>Equally important, there was minimal insight into how tactile, auditory, and braille modalities independently supported users' cognitive and emotional engagement with data.</p>
        <p><strong>My Specific Contributions:</strong> I co-designed the experimental methodology, developed theoretical frameworks for accessible AI-human interaction, designed and implemented user experience protocols, and performed comprehensive qualitative analysis of user interactions.</p>
        
        <h2>Research Objective</h2>
        <p><strong>How can multimodal and AI-driven systems enable independent, trustworthy, and cognitively accessible data interpretation for BLV users?</strong></p>
        
        <h2>Research Strategy Overview</h2>
        <p>Working closely with the research team, I helped structure the investigation into two sequential studies that informed each other:</p>
        <table>
          <tr>
            <th>Study</th>
            <th>Key Focus</th>
            <th>Methods Employed</th>
          </tr>
          <tr>
            <td>Study 1</td>
            <td>User interaction with AI-generated descriptions (maidrAI)</td>
            <td>Semi-Structured Interviews, Thematic Analysis, AUS Survey</td>
          </tr>
          <tr>
            <td>Study 2</td>
            <td>User performance across tactile, sonified, and braille data representations</td>
            <td>Gesture Analysis, Demographic Survey, Statistical Analysis (Friedman Test)</td>
          </tr>
        </table>
        
        <img src="/img/maidr/maidrai.jpg" alt="System architecture diagram showing the MAIDR AI system overview. The diagram illustrates how the multimodal system processes data through multiple channels: AI-generated text descriptions, tactile representations, sonified audio, and braille outputs. The system includes components for data upload, modality selection, and customizable accessibility options. The architecture demonstrates how the platform enables blind and low-vision users to independently interpret complex data visualizations through their preferred sensory modality." class="case-study-image mb-3" />
        
        <h2>Study 1: Evaluating AI-Generated Multimodal Descriptions (maidrAI)</h2>
        <h3>Problem Framing</h3>
        <p>Despite LLMs' potential to describe data, trust and personalization remained critical unknowns:</p>
        <ul>
          <li>Would users accept the AI's interpretation at face value?</li>
          <li>How might users want to prompt, edit, or verify descriptions?</li>
          <li>Could verbose AI outputs increase cognitive fatigue?</li>
        </ul>
        
        <h3>My Contributions</h3>
        <p>I co-designed the experimental methodology and conducted extensive user studies to evaluate multimodal LLM interactions with blind and low-vision users. My specific contributions included:</p>
        <ul>
          <li><strong>Theoretical Framework Development:</strong> Developed the theoretical framework for accessible AI-human interaction</li>
          <li><strong>User Experience Protocol Design:</strong> Designed and implemented comprehensive user experience protocols</li>
          <li><strong>Qualitative Analysis:</strong> Performed comprehensive qualitative analysis of user interactions and behaviors</li>
        </ul>
        
        <h3>Methodological Approach</h3>
        <p><strong>Mixed Methods Design:</strong><br>
        I contributed to blending qualitative exploration with quantitative usability metrics to triangulate user experience:</p>
        <table>
          <tr>
            <th>Data Type</th>
            <th>Method</th>
            <th>Purpose</th>
          </tr>
          <tr>
            <td>Qualitative</td>
            <td>Semi-Structured Interviews</td>
            <td>Capture personalization needs, verification strategies</td>
          </tr>
          <tr>
            <td>Qualitative</td>
            <td>Thematic Analysis</td>
            <td>Derive design principles from user narratives</td>
          </tr>
          <tr>
            <td>Quantitative</td>
            <td>Accessible Usability Scale (AUS)</td>
            <td>Quantify satisfaction, usability, cognitive load</td>
          </tr>
        </table>
        
        <h3>Semi-Structured Interviews</h3>
        <p>After participants explored the maidrAI system, I conducted semi-structured interviews focusing on:</p>
        <ul>
          <li>How they adapted or prompted the AI</li>
          <li>Strategies they used to verify output trustworthiness</li>
          <li>Preferences around description styles and length</li>
        </ul>
        <p><strong>Emergent Themes:</strong></p>
        <ul>
          <li><strong>Layered Summaries:</strong> Users preferred quick summaries with the option to drill down into detail.</li>
          <li><strong>Verification Rituals:</strong> Users routinely "double-checked" AI-generated information.</li>
          <li><strong>Cognitive Load:</strong> Verbosity increased mental effort, especially when navigating complex datasets.</li>
        </ul>
        
        <img src="/img/maidr/histcognition.jpg" alt="Bar chart showing histogram cognition study results comparing user performance across different data visualization modalities. The chart displays accuracy rates for tactile, sonified, and braille representations of statistical data, with error bars indicating confidence intervals. Results show that braille users achieved highest accuracy but took longer, while sonification users were fastest but had moderate accuracy. This visualization demonstrates the trade-offs between speed and precision across different accessibility modalities for data interpretation." class="case-study-image mb-3" />
        
        <h3>Accessible Usability Scale (AUS) Survey</h3>
        <p>Quantitative triangulation confirmed qualitative insights:</p>
        <ul>
          <li><strong>Cognitive Overload:</strong> Participants rated maidrAI lower on cognitive simplicity.</li>
          <li><strong>Task Completion:</strong> Higher scores for independence but lower scores for efficiency.</li>
        </ul>
        <p>This validated the urgent need for adaptive AI responses based on user cognitive bandwidth.</p>
        
        <h3>Key Insights from Study 1</h3>
        <ul>
          <li>AI outputs must be modular and customizable for BLV users to maintain autonomy.</li>
          <li>Trust-building in AI requires transparency and control, not just accuracy.</li>
          <li>Cognitive load is a UX barrier, and reducing it should be a primary design goal.</li>
        </ul>
        
        <h2>Study 2: Recognizing Statistical Properties Through Multimodal Interaction</h2>
        <h3>Problem Framing</h3>
        <p>Beyond textual descriptions, how well could BLV users interpret statistical properties (like skewness and modality) through tactile, sonified, or braille data representations?</p>
        <p>The team reasoned that performance differences across modalities could inform adaptive, user-selectable multimodal systems.</p>
        
        <h3>My Central Role and Contributions</h3>
        <p>I played a central role in shaping and executing this research investigating how BLV individuals conceptualize statistical concepts through non-visual representations. My comprehensive contributions included:</p>
        
        <h4>Methodology and Protocol Development</h4>
        <ul>
          <li><strong>Co-developed experimental protocol:</strong> Working closely with the PI to ensure methodological rigor</li>
          <li><strong>Research materials creation:</strong> Led the creation of comprehensive research materials and study instruments</li>
          <li><strong>STG materials development:</strong> Designed and created the tactile STG (swell-form) materials for the study</li>
        </ul>
        
        <h4>Study Implementation and Data Collection</h4>
        <ul>
          <li><strong>Protocol execution:</strong> Spearheaded the implementation of the study protocol</li>
          <li><strong>Participant sessions:</strong> Conducted detailed sessions with BLV participants across three non-visual modalities (STGs, BDPs, sonification)</li>
          <li><strong>Data collection design:</strong> Designed and implemented the data collection instruments</li>
          <li><strong>Procedure documentation:</strong> Created detailed documentation of experimental procedures for future replication</li>
        </ul>
        
        <h4>Analysis and Research Leadership</h4>
        <ul>
          <li><strong>Quantitative analysis:</strong> Performed sophisticated quantitative analysis of accuracy metrics, completion times, and confidence levels</li>
          <li><strong>Qualitative analysis:</strong> Conducted in-depth qualitative analysis of participants' gesture patterns and learning strategies</li>
          <li><strong>Coding framework development:</strong> Developed the coding framework for gesture analysis</li>
          <li><strong>Manuscript leadership:</strong> Led the manuscript development process, synthesizing complex findings into a cohesive narrative highlighting unique cognitive strategies employed by BLV learners</li>
        </ul>
        
        <h3>Methodological Approach</h3>
        <p><strong>Behavioural + Performance Data</strong><br>
        I expanded the research approach from self-reported data to observable behaviours and performance outcomes.</p>
        <table>
          <tr>
            <th>Data Type</th>
            <th>Method</th>
            <th>Purpose</th>
          </tr>
          <tr>
            <td>Behavioural</td>
            <td>Gesture Analysis</td>
            <td>Map tactile and braille exploration strategies</td>
          </tr>
          <tr>
            <td>Behavioural</td>
            <td>Playback Tracking</td>
            <td>Analyze auditory navigation behaviour</td>
          </tr>
          <tr>
            <td>Performance</td>
            <td>Accuracy, Confidence, Response Time</td>
            <td>Evaluate task success across modalities</td>
          </tr>
          <tr>
            <td>Statistical</td>
            <td>Friedman Test</td>
            <td>Assess modality differences significance</td>
          </tr>
        </table>
        
        <h3>Gesture Analysis and Playback Tracking</h3>
        <p>As part of my comprehensive research design, I:</p>
        <ul>
          <li>Designed 9 histograms representing different statistical shapes</li>
          <li>Created tactile and braille diagrams (swell-form paper) and sonified versions</li>
          <li>Developed systematic recording protocols for:
            <ul>
              <li>Hand movement patterns (start points, looping, speed changes)</li>
              <li>Sonification playback repetitions and edits</li>
            </ul>
          </li>
        </ul>
        <p><strong>Behavioral Findings from my Analysis:</strong></p>
        <ul>
          <li>Tactile diagrams promoted methodical, slow scanning patterns</li>
          <li>Sonification prompted rapid comparisons but sometimes missed fine details</li>
          <li>Braille readers relied heavily on numeric precision but struggled with "big-picture" trends</li>
        </ul>
        
        <img src="/img/maidr/maidr-hands.jpg" alt="Photograph showing a participant's hands exploring a tactile data visualization using swell-form paper. The image captures the participant's fingers tracing over raised patterns representing statistical data, demonstrating the hands-on approach to data interpretation. The tactile diagram shows a histogram with raised bars of varying heights, allowing the participant to feel the distribution of data points. This image illustrates the physical interaction method used in the study to understand how blind and low-vision users interpret statistical information through touch." class="case-study-image mb-3" />
        
        <h3>Quantitative Performance Analysis</h3>
        <p>Through my sophisticated analytical approach, I used the Friedman Test to compare user performance across modalities:</p>
        <table>
          <tr>
            <th>Modality</th>
            <th>Accuracy</th>
            <th>Response Time</th>
            <th>Confidence</th>
          </tr>
          <tr>
            <td>Tactile</td>
            <td>Moderate</td>
            <td>Slow</td>
            <td>High</td>
          </tr>
          <tr>
            <td>Sonification</td>
            <td>Moderate</td>
            <td>Fast</td>
            <td>Moderate</td>
          </tr>
          <tr>
            <td>Braille</td>
            <td>High</td>
            <td>Slow</td>
            <td>Moderate</td>
          </tr>
        </table>
        <p><strong>Interpretation:</strong></p>
        <ul>
          <li>No statistically significant difference in raw accuracy.</li>
          <li>Significant modality-driven differences in confidence and speed.</li>
          <li>Tactile representations improved comprehension but demanded higher cognitive effort.</li>
        </ul>
        
        <h2>Integrating Findings into Design</h2>
        <p>Based on our collective findings about user diversity, I contributed to the conclusion that a static one-size-fits-all modality would fail.<br>
        Instead, working with the team, I helped design a dynamic dashboard prototype that lets users:</p>
        <ul>
          <li>Upload datasets</li>
          <li>Choose between tactile, sonified, or text outputs</li>
          <li>Customize verbosity, feedback, and navigation style</li>
        </ul>
        
        <img src="/img/maidr/dashboard.jpg" alt="Screenshot of an interactive web dashboard for accessible data visualization. The interface shows a clean, modern design with multiple panels displaying different types of data charts and graphs. The dashboard includes controls for switching between tactile, sonified, and text-based data representations, with accessibility options prominently displayed. Users can upload datasets and customize their preferred modality for data interpretation. The interface demonstrates how the MAIDR system provides multiple ways for blind and low-vision users to interact with and understand complex data visualizations." class="case-study-image mb-3" />
        
        <h2>Reflections on Research Process</h2>
        <h3>What Worked</h3>
        <ul>
          <li>Sequential mixed methods let qualitative insights directly inform quantitative design</li>
          <li>Behavioral observation + performance metrics revealed subtleties not captured in self-report</li>
          <li>Collaborative design with BLV researchers and users grounded every decision in lived experience</li>
        </ul>
        
        <h3>Challenges</h3>
        <ul>
          <li>Managing cognitive load across different modalities required delicate balancing</li>
          <li>Interpreting non-verbal behavioral data (gestures, replays) demanded careful cross-validation</li>
        </ul>
        
        <h3>Personal Learning</h3>
        <p>This collaborative project solidified my understanding that trust, control, and personalization must drive accessible AI design, especially when navigating cognitive and emotional complexity in assistive tech. Working as part of a research team taught me the value of complementary expertise and diverse perspectives in tackling complex accessibility challenges.</p>
        
        <h2>Final Outcome</h2>
        <p>Through my contributions to this collaborative effort, we achieved:</p>
        <ul>
          <li>Generated empirical insights on modality-specific strengths and weaknesses</li>
          <li>Co-designed a user-driven dashboard enabling customizable multimodal data access</li>
          <li>Advanced the broader mission of equitable, ethical, and autonomous AI accessibility solutions</li>
        </ul>
        
        <a href="https://xabilitylab.shinyapps.io/a11y_dashboard/" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
          View Interactive Dashboard
        </a>
      `
    },
    'clearminds': {
      title: 'ClearMinds: Mental Health UX During the Pandemic',
      tags: ['Mental Health', 'User Research', 'Pandemic Response', 'Digital Wellness'],
      timeline: 'March 2020 - December 2020',
      image: '/img/clearmindsbanner.jpg',
      content: `
        <h2>Project Context</h2>
        <p>As the COVID-19 pandemic unfolded in 2020, mental health challenges surged while traditional support systems became inaccessible. ClearMinds emerged as a response to this crisis, designed to provide accessible, trust-centered mental health support through digital platforms.</p>
        
        <h2>Research Challenge</h2>
        <p>The pandemic created unique mental health challenges:</p>
        <ul>
          <li>Increased isolation and anxiety</li>
          <li>Disrupted routines and support systems</li>
          <li>Overwhelmed mental health services</li>
          <li>Need for immediate, accessible support</li>
        </ul>
        
        <h2>Design Philosophy</h2>
        <p>ClearMinds was built around two core principles:</p>
        <ul>
          <li><strong>Trust-Centered Design</strong>: Recognizing that mental health support requires deep trust between user and system</li>
          <li><strong>Emotional Awareness</strong>: Helping users develop better understanding of their emotional states and patterns</li>
        </ul>
        
        <h2>Research Methodology</h2>
        <h3>1. Rapid User Research</h3>
        <p>Given the urgent nature of the pandemic, I conducted rapid user research including:</p>
        <ul>
          <li>Remote interviews with individuals experiencing pandemic-related stress</li>
          <li>Survey studies on mental health needs during lockdown</li>
          <li>Analysis of existing mental health apps and their limitations</li>
        </ul>
        
        <h3>2. Participatory Design</h3>
        <p>Worked directly with mental health professionals and individuals with lived experience to co-design the platform features and interaction patterns.</p>
        
        <h2>Key Design Decisions</h2>
        <h3>1. Balancing Structure and Flexibility</h3>
        <p>The platform needed to provide structured progress tracking while allowing for the unpredictable nature of mental health journeys. This led to:</p>
        <ul>
          <li>Flexible goal-setting that adapts to user capacity</li>
          <li>Progress tracking that celebrates small wins</li>
          <li>Personalized check-in frequencies</li>
        </ul>
        
        <h3>2. Trust-Building Features</h3>
        <ul>
          <li>Transparent data handling and privacy controls</li>
          <li>User control over sharing and visibility</li>
          <li>Clear communication about system limitations</li>
        </ul>
        
        <h3>3. Emotional Awareness Tools</h3>
        <ul>
          <li>Mood tracking with contextual prompts</li>
          <li>Pattern recognition and gentle insights</li>
          <li>Mindfulness and grounding exercises</li>
        </ul>
        
        <h2>Technical Implementation</h2>
        <p>The ClearMinds platform included:</p>
        <ul>
          <li>Responsive web application for cross-device access</li>
          <li>End-to-end encrypted data storage</li>
          <li>Integration with crisis support resources</li>
          <li>Accessibility features for users with disabilities</li>
        </ul>
        
        <h2>User Testing and Iteration</h2>
        <p>Throughout 2020, we conducted multiple rounds of user testing:</p>
        <ul>
          <li>Remote usability testing with individuals experiencing mental health challenges</li>
          <li>Feedback sessions with mental health professionals</li>
          <li>A/B testing of different interaction patterns</li>
          <li>Longitudinal studies on user engagement and outcomes</li>
        </ul>
        
        <h2>Research Findings</h2>
        <p>Key insights from the ClearMinds project:</p>
        <ul>
          <li>Trust is the foundation of effective mental health technology</li>
          <li>Personalization must extend beyond features to include interaction patterns</li>
          <li>Small, consistent interactions are more effective than intensive interventions</li>
          <li>Community features require careful design to avoid comparison and judgment</li>
        </ul>
        
        <h2>Impact and Outcomes</h2>
        <p>ClearMinds served hundreds of users during the pandemic, providing:</p>
        <ul>
          <li>Accessible mental health support during crisis</li>
          <li>Tools for emotional self-awareness and regulation</li>
          <li>Connection to professional resources when needed</li>
          <li>Research insights that informed future mental health technology</li>
        </ul>
        
        <h2>Lessons Learned</h2>
        <p>This project taught me about:</p>
        <ul>
          <li>The critical importance of trauma-informed design</li>
          <li>How to balance user agency with supportive guidance</li>
          <li>The role of technology in crisis response</li>
          <li>Ethical considerations in mental health technology</li>
        </ul>
        
        <p>The experience reinforced my commitment to user-centered design and the power of technology to support human wellbeing when thoughtfully implemented.</p>
      `
    }
  };

  const study = caseStudies[id || ''];

  if (!study) {
    return (
      <div className="App">
        <section className="hero">
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">Case Study Not Found</h1>
              <p className="hero-subtitle">The requested case study could not be found.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="App">
      <IdentityCard />
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content animate-fade-in-up">
            <h1 className="hero-title">{study.title}</h1>
            <div className="card-tags mb-3">
              {study.tags.map((tag: string) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
            <p className="hero-subtitle">{study.timeline}</p>
          </div>
        </div>
      </section>

      {/* Case Study Image */}
      <section className="section-compact">
        <div className="container">
          <div className="text-center">
            <img 
              src={study.image} 
              alt={study.title}
              className="case-study-image w-full rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="section">
        <div className="container">
          <div className="card">
            <div 
              className="prose"
              dangerouslySetInnerHTML={{ __html: study.content }}
            />
          </div>
        </div>
      </section>

      {/* Back to Projects */}
      <section className="section-compact bg-secondary">
        <div className="container">
          <div className="text-center">
            <a href="/projects" className="btn btn-primary">
              ← Back to Projects
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudy;
